import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import useOrderData from "@/hooks/data/use-orders-data";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrderSummaryMessages } from "@/messages/order-details/order-summary.messages";

type Props = {
  messages: OrderSummaryMessages;
  order: IOrder | null;
};

function OrderSummary({ messages, order }: Props) {
  const data = useOrderData({ order });
  const {
    orderFreight,
    orderDiscount,
    orderCharges,
    orderAmount,
    formattedTotalAmount,
  } = data;

  const formatCurrency = (value: number) =>
    value.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const shippingAgentService =
    (order?.shippingAgentService || (data as any)?.shippingAgentService || "")
      .toString()
      .trim();

  const MAX_SHIPPING_SERVICE_LENGTH = 20;
  const TRUNCATE_SUFFIX = "...";
  const displayShippingAgentService =
    shippingAgentService && shippingAgentService.length > MAX_SHIPPING_SERVICE_LENGTH
      ? `${shippingAgentService.slice(0, MAX_SHIPPING_SERVICE_LENGTH - TRUNCATE_SUFFIX.length)}${TRUNCATE_SUFFIX}`
      : shippingAgentService;

  return (
    <>
      <MDBox mb={2}>
        <MDTypography variant="h6" fontWeight="medium">
          {messages.orderSummaryTitle}
        </MDTypography>
      </MDBox>

      <MDBox display="flex" justifyContent="space-between" mb={0.5}>
        <MDTypography variant="button" fontWeight="regular" color="text">
          {messages.productTotalLabel}
        </MDTypography>
        <MDBox ml={1}>
          <MDTypography variant="body2" fontWeight="medium">
            {formatCurrency(orderAmount)}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox display="flex" justifyContent="space-between" mb={0.5}>
        <MDTypography variant="button" fontWeight="regular" color="text">
          {messages.discountsLabel}
        </MDTypography>
        <MDBox ml={1}>
          <MDTypography variant="body2" fontWeight="medium">
            {formatCurrency(orderDiscount)}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox display="flex" justifyContent="space-between" mb={0.5}>
        <MDTypography variant="button" fontWeight="regular" color="text">
          {messages.deliveryFeeLabel}
        </MDTypography>
        <MDBox ml={1}>
          <MDTypography variant="body2" fontWeight="medium">
            {displayShippingAgentService ? `(${displayShippingAgentService}) ` : ""}
            {formatCurrency(orderFreight)}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox display="flex" justifyContent="space-between" mb={0.5}>
        <MDTypography variant="button" fontWeight="regular" color="text">
          {messages.chargesLabel}
        </MDTypography>
        <MDBox ml={1}>
          <MDTypography variant="body2" fontWeight="medium">
            {formatCurrency(orderCharges)}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox display="flex" justifyContent="space-between" mt={3}>
        <MDTypography variant="body1" fontWeight="light" color="text">
          {messages.totalPriceLabel}
        </MDTypography>
        <MDBox ml={1}>
          <MDTypography variant="body1" fontWeight="medium">
            {formattedTotalAmount}
          </MDTypography>
        </MDBox>
      </MDBox>
    </>
  );
}

export default OrderSummary;
